
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Edit, Share, MoreHorizontal, Tag, Calendar, Building, Download, Trash2, SquarePen, BarChart2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useScrapeCampaignDeliverables } from "@/domains/content";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from '@/components/status/StatusBadge';
import { useOrganization } from '@/contexts/OrganizationContext';

interface Props {
  campaign: any;
  onEdit: () => void;
}

const CampaignHeader: React.FC<Props> = ({ campaign, onEdit }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const isBrandOrg = currentOrganization?.organization_type === 'brand';
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { scrapeCampaign, isScraping } = useScrapeCampaignDeliverables(
    campaign?.id,
    campaign?.organization_id
  );

  const handleFetchMetrics = () => {
    scrapeCampaign();
  };

  const handleDeleteCampaign = async () => {
    const { error } = await supabase
      .from('campaigns')
      .delete()
      .eq('id', campaign.id);

    if (error) {
      console.error("Delete campaign error:", error);
      toast({
        title: "Error",
        description: "Failed to delete campaign: " + error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Campaign deleted successfully",
      });
      navigate('/campaigns');
    }
  };

  const handleShare = () => {
    const shareUrl = window.location.href;
    const campaignName = campaign?.name || "Campaign";
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast({
        title: "Link Copied!",
        description: `Link for "${campaignName}" has been copied to your clipboard.`,
      });
    }).catch((err) => {
      console.error("Failed to copy link: ", err);
      toast({
        title: "Sharing Failed",
        description: "Could not copy link to clipboard.",
        variant: "destructive",
      });
    });
  };

  const formatDateRange = (start?: string, end?: string) => {
    if (!start && !end) return "Not set";

    const formatDateStr = (dateStr?: string, includeYear = true) => {
      if (!dateStr || dateStr === "Not set") return "";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: includeYear ? 'numeric' : undefined
      });
    };

    if (start && end && start !== "Not set" && end !== "Not set") {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const sameYear = startDate.getFullYear() === endDate.getFullYear();
        return `${formatDateStr(start, !sameYear)} - ${formatDateStr(end, true)}`;
      }
    }

    if (start && start !== "Not set") return formatDateStr(start, true);
    if (end && end !== "Not set") return formatDateStr(end, true);
    return "Not set";
  };

  const getDurationDays = (start?: string, end?: string) => {
    if (!start || !end || start === "Not set" || end === "Not set") return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

    const diffTime = endDate.getTime() - startDate.getTime();
    if (diffTime < 0) return null;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // inclusive
    return diffDays;
  };

  const getCampaignType = () => {
    if (campaign.goal_type) {
      return campaign.goal_type.charAt(0).toUpperCase() + campaign.goal_type.slice(1);
    }
    if (campaign.brand?.industry && campaign.brand.industry !== "Not specified") {
      return campaign.brand.industry;
    }
    return "Awareness";
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        {/* Left Side: Status, Name and Description */}
        <div className="space-y-1">
          <StatusBadge status={campaign.status} showDot />
          <h1 className="text-[48px] font-playfair font-bold tracking-tight text-slate-900 mt-2">
            {campaign.name}
          </h1>
          {campaign.content_requirements && (
            <div className="flex flex-wrap gap-2 mt-3">
              {(Array.isArray(campaign.content_requirements)
                ? campaign.content_requirements
                : campaign.content_requirements.split(/[\s,]+/).filter(Boolean)
              ).map((tag: string, index: number) => (
                <span
                  key={index}
                  className="text-sm text-blue-600 font-medium px-2 py-1 bg-blue-50 rounded-lg"
                >
                  #{tag.replace(/^#/, '')}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right Side: Action Buttons */}
        <div className="flex items-center gap-3 self-start mt-2 md:mt-0 flex-wrap">
          <Button
            onClick={() => navigate(`/analytics?campaignId=${campaign.id}`)}
            variant="outline"
            className="rounded-xl px-4 py-2"
          >
            <BarChart2 className="w-4 h-4" />
            View Analytics
          </Button>

          {/* <Button
            onClick={onEdit}
            className="rounded-xl px-4 py-2"
          >
            <Edit className="w-4 h-4" />
            Edit Campaign
          </Button> */}

          <Button
            onClick={handleShare}
            className="rounded-xl px-4 py-2"
          >
            <Share className="w-4 h-4" />
            Share
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-xl p-2.5 cursor-pointer focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[320px] rounded-[24px] p-2 border border-slate-100 shadow-xl bg-white font-['Hanken_Grotesk',sans-serif] focus:outline-none">

              <div className="h-px bg-slate-100 my-1.5" />

              <DropdownMenuItem
                onClick={handleFetchMetrics}
                disabled={isScraping}
                className="p-3 flex items-start gap-4 hover:bg-slate-50 transition-colors cursor-pointer rounded-2xl focus:bg-[#FAF5FF] outline-none border-none disabled:opacity-50 disabled:pointer-events-none"
              >
                <div className="w-12 h-12 rounded-[16px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#A655F8]/5">
                  {isScraping ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-semibold text-slate-800">
                    {isScraping ? "Fetching Metrics..." : "Fetch Metrics"}
                  </div>
                  <div className="text-[13px] text-slate-400 font-normal mt-0.5">
                    {isScraping ? "Updating campaign deliverables..." : "Get latest campaign data"}
                  </div>
                </div>
              </DropdownMenuItem>

              <div className="h-px bg-slate-100 my-1.5" />

              <DropdownMenuItem onClick={onEdit} className="p-3 flex items-start gap-4 hover:bg-slate-50 transition-colors cursor-pointer rounded-2xl focus:bg-[#EFF6FF] outline-none border-none">
                <div className="w-12 h-12 rounded-[16px] bg-[#EFF6FF] flex items-center justify-center text-[#3B82F6] shrink-0 border border-[#3B82F6]/5">
                  <SquarePen className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-semibold text-slate-800">Edit Campaign</div>
                  <div className="text-[13px] text-slate-400 font-normal mt-0.5">Update campaign details</div>
                </div>
              </DropdownMenuItem>

              <div className="h-px bg-slate-100 my-1.5" />

              <DropdownMenuItem onClick={() => setDeleteConfirmOpen(true)} className="p-3 flex items-start gap-4 hover:bg-slate-50 transition-colors cursor-pointer rounded-2xl focus:bg-[#FEF2F2] outline-none border-none">
                <div className="w-12 h-12 rounded-[16px] bg-[#FEF2F2] flex items-center justify-center text-[#EF4444] shrink-0 border border-[#EF4444]/5">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-semibold text-[#EF4444]">Delete Campaign</div>
                  <div className="text-[13px] text-slate-400 font-normal mt-0.5">Remove campaign permanently</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Delete Confirmation Alert Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="rounded-[24px] border-slate-100 p-6 font-['Hanken_Grotesk',sans-serif] focus:outline-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-800">Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 text-sm mt-2">
              Are you sure you want to delete this campaign permanently? This action cannot be undone and will delete all assigned deliverables, expenses, and task history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex items-center justify-end gap-3 pt-4 border-none">
            <AlertDialogCancel className="rounded-xl h-11 px-6 hover:bg-slate-50 text-slate-700 border-slate-200 font-semibold text-sm transition-colors">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCampaign}
              className="rounded-xl h-11 px-6 font-semibold text-sm"
            >
              Delete Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Info Cards (Brand, Campaign Type, Date Period) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-0 mt-6 pt-6 border-t border-slate-100">
        {/* Brand Section */}
        {!isBrandOrg && (
          <div className="flex items-center gap-3 sm:pr-8">
            <div className="w-12 h-12 flex items-center justify-center bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex-shrink-0">
              {campaign.brand?.logo_url ? (
                <img
                  src={campaign.brand.logo_url}
                  alt={campaign.brand.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building className="w-5 h-5 text-slate-400" />
              )}
            </div>
            <div>
              <div className="text-[1rem] font-semibold text-slate-900">
                {campaign.brand?.name || "Unknown Brand"}
              </div>
              <div className="text-sm text-slate-400 font-normal">Brand</div>
            </div>
          </div>
        )}

        {/* Campaign Type Section */}
        <div
          className={`flex items-center gap-3 ${
            !isBrandOrg ? 'sm:px-8 sm:border-l sm:border-slate-100' : 'sm:pr-8'
          }`}
        >
          <div className="w-12 h-12 flex items-center justify-center bg-[#A655F8]/10 rounded-xl flex-shrink-0">
            <Tag className="w-5 h-5 text-[#A655F8]" />
          </div>
          <div>
            <div className="text-[1rem] font-semibold text-slate-900">
              {getCampaignType()}
            </div>
            <div className="text-sm text-slate-400 font-normal">Industry</div>
          </div>
        </div>

        {/* Campaign Period Section */}
        <div className="flex items-center gap-3 sm:pl-8 sm:border-l sm:border-slate-100">
          <div className="w-12 h-12 flex items-center justify-center bg-[#A655F8]/10 rounded-xl flex-shrink-0">
            <Calendar className="w-5 h-5 text-[#A655F8]" />
          </div>
          <div>
            <div className="text-[1rem] font-semibold text-slate-900">
              {formatDateRange(campaign.startDate || campaign.start_date, campaign.endDate || campaign.end_date)}
            </div>
            <div className="text-sm text-slate-400 font-normal">
              {(() => {
                const days = getDurationDays(campaign.startDate || campaign.start_date, campaign.endDate || campaign.end_date);
                return days ? `Campaign Period (${days} days)` : "Campaign Period";
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignHeader;
